import { Word } from "@/types";

/**
 * Convert words to Quizlet-ready text: one card per line,
 * Hebrew ⇥(tab) English. This matches Quizlet's default custom import
 * ("Tab" between term and definition, "New line" between cards).
 */
export function wordsToQuizletText(words: Word[]): {
  text: string;
  exported: number;
  skipped: number;
} {
  const lines: string[] = [];
  let skipped = 0;

  for (const word of words) {
    const hebrew = word.hebrew.text.trim();
    const translation = (
      word.translations.en?.text ||
      word.translations.ru?.text ||
      ""
    ).trim();

    if (!hebrew || !translation) {
      skipped++;
      continue;
    }

    // Tabs/newlines inside the data would corrupt the format — flatten them.
    lines.push(
      `${hebrew.replace(/[\t\n]+/g, " ")}\t${translation.replace(/[\t\n]+/g, " ")}`
    );
  }

  return { text: lines.join("\n"), exported: lines.length, skipped };
}

/**
 * Trigger a browser download of a plain-text file.
 */
export function downloadTextFile(filename: string, content: string): void {
  // BOM so Excel/Notepad detect UTF-8 and render Hebrew correctly
  const blob = new Blob(["﻿" + content], {
    type: "text/plain;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Sanitize a study set / category name into a safe filename.
 */
export function toSafeFilename(name: string): string {
  const safe = name
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return safe || "shoresh-words";
}

// ===========================================
// File formats (Quizlet accepts xlsx/docx/pdf uploads)
// ===========================================

/** Word pairs ready for tabular export. */
function toRows(words: Word[]): [string, string][] {
  return words
    .map((w): [string, string] => [
      w.hebrew.text.trim(),
      (w.translations.en?.text || w.translations.ru?.text || "").trim(),
    ])
    .filter(([h, t]) => h && t);
}

/**
 * Excel export via SheetJS (lazy-loaded). Best format for Quizlet import —
 * logical Unicode order, unambiguous two-column structure.
 */
export async function exportAsXlsx(words: Word[], name: string): Promise<void> {
  const XLSX = await import("xlsx");
  const rows = [["Hebrew", "English"], ...toRows(words)];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 24 }, { wch: 32 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Words");
  XLSX.writeFile(wb, `${toSafeFilename(name)}.xlsx`);
}

/**
 * Word (.docx) export via the docx package (lazy-loaded).
 * Two-column table; Hebrew cells are RTL.
 */
export async function exportAsDocx(words: Word[], name: string): Promise<void> {
  const {
    Document, Packer, Paragraph, Table, TableCell, TableRow,
    TextRun, WidthType, AlignmentType,
  } = await import("docx");

  const headerRow = new TableRow({
    children: ["Hebrew", "English"].map(
      (h) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
        })
    ),
  });

  const rows = toRows(words).map(
    ([hebrew, english]) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                bidirectional: true,
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({ text: hebrew, rightToLeft: true, size: 28 })],
              }),
            ],
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: english, size: 24 })] })],
          }),
        ],
      })
  );

  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({ children: [new TextRun({ text: name, bold: true, size: 32 })] }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [headerRow, ...rows],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, `${toSafeFilename(name)}.docx`);
}

/**
 * PDF export via jsPDF with an embedded Hebrew font (lazy-loaded).
 *
 * PDFs store VISUAL glyph order, so Hebrew is written reversed (niqqud
 * stripped) to render correctly. Fine for printing/reading; for Quizlet
 * import, xlsx/docx are more reliable since their text stays in logical order.
 */
export async function exportAsPdf(words: Word[], name: string): Promise<void> {
  const [{ jsPDF }, { NOTO_SANS_HEBREW_BASE64 }] = await Promise.all([
    import("jspdf"),
    import("./fonts/notoSansHebrew"),
  ]);

  const doc = new jsPDF();
  doc.addFileToVFS("NotoSansHebrew.ttf", NOTO_SANS_HEBREW_BASE64);
  doc.addFont("NotoSansHebrew.ttf", "NotoSansHebrew", "normal");

  // Visual-order Hebrew: strip niqqud (combining marks break naive reversal —
  // Hebrew is non-cursive so plain reversal is correct), then reverse.
  const visualHebrew = (s: string) =>
    Array.from(s.replace(/[֑-ׇ]/g, "")).reverse().join("");

  const pageH = doc.internal.pageSize.getHeight();
  const rightCol = 190; // Hebrew right-aligned
  let y = 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(name, 20, y);
  y += 10;

  doc.setFontSize(12);
  for (const [hebrew, english] of toRows(words)) {
    if (y > pageH - 20) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("NotoSansHebrew", "normal");
    doc.text(visualHebrew(hebrew), rightCol, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.text(english, 20, y, { maxWidth: 100 });
    y += 9;
  }

  doc.save(`${toSafeFilename(name)}.pdf`);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
